import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import theme from "../styles/theme";
import { getData, initPasswordFromLink } from "../api";

export default function PremierAccesPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const uid = searchParams.get("uid") || "";
  const exp = searchParams.get("exp") || "";
  const sig = searchParams.get("sig") || "";

  const [username, setUsername] = useState<string>("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  const isLinkValidShape = useMemo(() => {
    return Boolean(uid && exp && sig);
  }, [uid, exp, sig]);

  useEffect(() => {
    let mounted = true;
    setError("");

    if (!isLinkValidShape) return;

    (async () => {
      try {
        // On récupère juste le pseudo pour afficher une confirmation visuelle.
        const res: any = await getData("users", {
          where: `id='${String(uid).replace(/'/g, "''")}'`,
          fields: ["id", "username", "role"],
          limit: 1,
        });
        const row = res?.data?.[0];
        if (mounted) setUsername(row?.username || "");
      } catch {
        // Silencieux: l'API peut refuser le GET selon la config.
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isLinkValidShape, uid]);

  const validation = useMemo(() => {
    if (!isLinkValidShape) return { ok: false, message: "Lien d'initialisation invalide." };
    if (!newPassword) return { ok: false, message: "Le mot de passe est requis." };
    if (newPassword.length < 8) return { ok: false, message: "Le mot de passe doit contenir au moins 8 caractères." };
    if (confirmPassword !== newPassword) return { ok: false, message: "La confirmation ne correspond pas." };
    return { ok: true, message: "" };
  }, [isLinkValidShape, newPassword, confirmPassword]);

  const handleInit = async () => {
    setError("");
    setSuccess("");

    if (!validation.ok) {
      setError(validation.message);
      return;
    }

    try {
      setLoading(true);
      const res: any = await initPasswordFromLink({
        userId: uid,
        exp,
        sig,
        newPassword,
      });

      if (res?.success) {
        setSuccess("Mot de passe défini avec succès. Tu peux te connecter.");
        setTimeout(() => navigate("/login"), 1200);
      } else {
        setError(res?.error || "Erreur lors de l'initialisation.");
      }
    } catch (e: any) {
      setError(e?.message || "Erreur lors de l'initialisation.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 140px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        pb: 10,
      }}
    >
      <Paper elevation={6} sx={{ width: "100%", maxWidth: 520, p: { xs: 3, sm: 4 } }}>
        <Stack spacing={2.5}>
          <Stack spacing={0.5}>
            <Typography variant="h2" sx={{ fontSize: { xs: "1.6rem", sm: "2rem" } }}>
              Premier accès
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Définis ton mot de passe personnel pour activer ton compte.
            </Typography>
          </Stack>

          {username ? (
            <Box
              sx={{
                borderRadius: 2,
                p: 2,
                bgcolor: theme.palette.background.paperColored,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Compte : <b>{username}</b>
              </Typography>
            </Box>
          ) : null}

          {!isLinkValidShape ? (
            <Alert severity="error">
              Lien d'initialisation manquant ou incomplet. Demande un nouveau lien au coach.
            </Alert>
          ) : null}

          <TextField
            label="Nouveau mot de passe"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            fullWidth
            helperText="Au moins 8 caractères."
            disabled={!isLinkValidShape || loading}
          />

          <TextField
            label="Confirmer le mot de passe"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            fullWidth
            disabled={!isLinkValidShape || loading}
          />

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleInit}
            disabled={!isLinkValidShape || loading}
            sx={{ py: 1.25, fontWeight: 700 }}
          >
            {loading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} color="inherit" />
                <span>Activation…</span>
              </Stack>
            ) : (
              "Activer mon compte"
            )}
          </Button>

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <Button variant="text" onClick={() => navigate("/login")}>
            Retour à la connexion
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
